<template>
  <div>
    <vue-simple-context-menu
      :elementId="'myUniqueId'"
      :options="options"
      :ref="'vueSimpleContextMenu'"
      @option-clicked="optionClicked"
    />

    <vuetable ref="vuetable"
      :css="tableCss"
      :api-url="`${apiUrl}/api/${url}`"
      :http-options="httpOptions"
      :fields="fields"
      :query-params="{ sort: 'sort', page: 'page', perPage: 'limit'}"
      :append-params="extraParams"
      pagination-path=""
      @vuetable:pagination-data="onPaginationData"
      @vuetable:load-error="checkError"
      :per-page="25"
    >
      <!-- USER CELLS -->
      <div slot="name-slot" slot-scope="props">
        <span class="ui link label" @click="$emit('details-clicked', props.rowData)">{{props.rowData.first_name}} {{props.rowData.last_name}}</span>
      </div>
      <div slot="gender-slot" slot-scope="props">
        <span v-if="props.rowData.gender === 'male'" class="ui label">M</span>
        <span v-else class="ui label">F</span>
      </div>
      <div slot="verified-slot" slot-scope="props">
        <span v-if="props.rowData.verified === true"><img src="../../assets/profile_verified.png"></span>
      </div>
      <div slot="pro-member-slot" slot-scope="props">
        <span v-if="props.rowData.pro_member === true"><img src="../../assets/profile_pro.png"></span>
      </div>
      <div slot="banned-member-slot" slot-scope="props">
        <span v-if="props.rowData.banned === true"><i class="fa fa-ban" aria-hidden="true"></i></span>
      </div>
      <div slot="user-actions-slot" slot-scope="props">
        <i class="action fas fa-ellipsis-v" @click.prevent.stop="handleClick($event, props.rowData)"></i>
      </div>

      <!-- EXERCISE CELLS -->
      <div slot="title-slot" slot-scope="props">
        <span class="ui link label" @click="$emit('details-clicked', props.rowData)">{{ props.rowData.title }}</span>
      </div>
      <div slot="secondary-slot" slot-scope="props">
        <span class="ui label">{{ getSecondaryMuscles(props.rowData.secondary_muscles) }}</span>
      </div>
      <div slot="video-slot" slot-scope="props">
        <span v-if="props.rowData.video === true"><i class="fa fa-video" aria-hidden="true"></i></span>
        <span v-else>-</span>
      </div>
      <div slot="exercise-actions-slot" slot-scope="props">
        <i class="action fas fa-ellipsis-v" @click.prevent.stop="handleClick($event, props.rowData)"></i>
      </div>

      <!-- REPORT CELLS -->
      <div slot="report-id-slot" slot-scope="props">
        <span class="ui link id" @click="$emit('details-clicked', { data: props.rowData, type: 'report' })">{{ props.rowData.id }}</span>
      </div>
      <div slot="report-count-slot" slot-scope="props">
        <span class="ui link" @click="$emit('details-clicked', { data: props.rowData, type: 'count' })">{{ props.rowData.total_count }}</span>
      </div>
      <div slot="report-user-slot" slot-scope="props">
        <span class="ui label link" @click="$emit('details-clicked', { data: props.rowData, type: 'reported_user' })">{{ props.rowData.reported_user.first_name }} {{ props.rowData.reported_user.last_name }} ({{props.rowData.reported_user.total_user_count}})</span>
      </div>
      <div slot="report-by-slot" slot-scope="props">
        <span class="ui label link" @click="$emit('details-clicked', { data: props.rowData, type: 'reported_by' })">{{ props.rowData.reported_by.first_name }} {{ props.rowData.reported_by.last_name }} ({{props.rowData.reported_by.total_user_count}})</span>
      </div>
      <div slot="report-user-static-slot" slot-scope="props">
        <span class="ui label">{{ props.rowData.user.first_name }} {{ props.rowData.user.last_name }} ({{props.rowData.user.total_user_count}})</span>
      </div>
      <div slot="report-reasons-slot" slot-scope="props">
        <span class="ui label">{{ formatReasons(props.rowData.reasons) }}</span>
      </div>
      <div slot="report-category-slot">
        <span class="ui label">POST</span>
      </div>

      <!-- CHALLENGE CELLS -->
      <div slot="challenge-image-slot" slot-scope="props">
        <span class="ui link label" @click="$emit('details-clicked', props.rowData)"><img :src="apiUrl + props.rowData.image" style="width: 24px"/></span>
      </div>
      <div slot="challenge-name-slot" slot-scope="props">
        <span class="ui link label" @click="$emit('details-clicked', props.rowData)">{{ props.rowData.title }}</span>
      </div>
      <div slot="challenge-category-slot" slot-scope="props">
        <span class="ui label">{{ props.rowData.category.toUpperCase() }}</span>
      </div>
      <div slot="challenge-completed-slot" slot-scope="props">
        <span class="ui label">{{ props.rowData.completed }}</span>
      </div>
      <div slot="challenges-actions-slot" slot-scope="props">
        <i class="action fas fa-ellipsis-v" @click.prevent.stop="handleClick($event, props.rowData)"></i>
      </div>
    </vuetable>

    <vuetable-pagination 
      ref="pagination"
      :css="paginationCss"
      @vuetable-pagination:change-page="onChangePage"></vuetable-pagination>
  </div>
</template>

<script>
import { Vuetable, VuetablePagination } from 'vuetable-2'
import { deleteExercise } from '@/api/requests';

export default {
  name: "DataTable",

  components: {
    Vuetable,
    VuetablePagination,
  },

  props: {
    url: {
      type: String,
      required: true,
    },

    fields: {
      type: Array,
      required: true
    },

    extraParams: {
      type: Object,
      default: () => {},
    },

    options: {
      type: Array,
      default: () => []
    }
  },

  data() {
    return {
      httpOptions: { headers: { Authorization: 'Bearer ' + this.$store.state.users.token } },
      tableCss: {
        tableWrapper: 'wotive-table',
        tableHeaderClass: 'wotive-table-header',
        tableBodyClass: 'mb-0',
        ascendingIcon: 'fa fa-chevron-up',
        descendingIcon: 'fa fa-chevron-down',
        sortableIcon: 'fa fa-sort',
      },
      paginationCss: {
        wrapperClass: 'pagination pull-right',
        activeClass: 'active',
        disabledClass: 'disabled',
        pageClass: 'page-item',
        linkClass: 'page-link',
        paginationClass: 'pagination',
        paginationInfoClass: 'float-left',
        dropdownClass: 'form-control',
        icons: {
          first: 'fa fa-chevron-left',
          prev: 'fa fa-chevron-left',
          next: 'fa fa-chevron-right',
          last: 'fa fa-chevron-right',
        }
      },
    };
  },

  methods: {
    onPaginationData(paginationData) {
      this.$refs.pagination.setPaginationData(paginationData);
    },

    onChangePage(page) {
      this.$refs.vuetable.changePage(page);
    },

    checkError(error) {
      // this.$store.dispatch('users/logout');
    },

    getSecondaryMuscles(value) {
      const muscles = JSON.parse(value);
      let output = '';
      if (muscles) {
        muscles.forEach(muscle => {
          if (output != '') {
            output += ", " + muscle.label;
          } else {
            output = muscle.label;
          }
        });
      }

      return output !== '' ? output : '-';
    },

    handleClick (event, item, row) {
      this.$refs.vueSimpleContextMenu.showMenu(event, item);
      this.selectedRow = row;
    },

    optionClicked (event) {
      const action = event['option']['action'];
      if (action === 'loadExerciseDetails') {
        this.loadExercise(event['item']);
      } else if (action === 'editExerciseDetails') {
        this.$store.dispatch('exercise/editExerciseClicked', event['item']);
      } else if (action === 'deleteExercise') {
        this.deleteExercise(event['item']);
      } else if (action == 'editChallenge') {
        this.$router.push({ name: 'ChallengeManagement', params: { creating: false, challenge: event.item } })
      } else if (action == 'awardChallenge') {
        this.$store.dispatch('challenges/awardChallengeClicked', event['item']);
      }
    },

    deleteExercise(exercise) {
      if (confirm('Are you sure you want to delete this exercise?')) {
        deleteExercise(exercise['id']).then(() => {
          this.$toasted.global.app_success({
            message: "Exercise has been deleted."
          });
          this.$store.dispatch('reloadTable');
        });
      }
    },

    formatReasons(reasons) {
      if (reasons) {
        reasons = reasons.replace("{", "");
        reasons = reasons.replace("}", "");
      }
      return reasons !== '' ? reasons.split(',').join(', ') : '-';
    }
  },

  computed: {
    apiUrl() {
      if (window.location.origin === 'http://localhost:8080') {
        return 'http://localhost:3000';
      }
      return window.location.origin;
    },

    reloadTable() {
      return this.$store.state.reload;
    }
  },

  watch: {
    extraParams() {
      this.$nextTick(function () {
        this.$refs.vuetable.refresh();
      });
    },

    reloadTable() {
      this.$nextTick(() => {
        this.$refs.vuetable.reload();
      })
    }
  }
}
</script>

<style lang="scss">
@import "@/assets/css/variables";

.vuetable-body-wrapper{
  padding-left: $spacing-base;
  padding-right: $spacing-base;
  table {
    border: 0;
    border-collapse: collapse;
    width: 100%;

    thead {
      background: $dark;
      color: #fff;
      height: 28px;

      th {
        text-align: left;
        padding-right: $spacing_base;
        font-weight: bold;
        text-transform: uppercase;
        font-size: .8em;
      }

      th:first-child {
        padding-left: $spacing_base;
      }
    }

    tbody {
      tr {
        height: 28px;
        color: $sec5;
        font-size: .8em;
      }
      
      tr:nth-child(even) {
        background-color: $sec0;
      }

      td:first-child {
        padding-left: $spacing_base;
        padding-right: 0;
      }
    }
  }
}

.vuetable-slot {
  button {
    float: right;
    margin-right: $spacing-small;
  }
  .verify-btn {
    width: 120px;
  }
}

.pagination {
  padding: $spacing-base;
  background: $dark !important;
  .page-item {
    color: #FFF;
    padding: 4px 8px 4px 8px;
    cursor: pointer;
    width: 20px;
    height: 20px;
  }
  .page-link {
    i {
      color: $sec3;
      cursor: pointer;
    }
  }
  .active {
    color: #FFF;
    background: $blue;
  }
  .disabled {
    i {
      color: $sec5 !important;
    }
  }
}

.link {
  color: $blue;
  cursor: pointer;
}

.center {
  text-align: center;
}

.action {
  cursor: pointer;
}

.id {
  width: 100px;
  text-overflow: ellipsis;
  display: block;
  overflow: hidden;
  white-space: nowrap;
}
</style>